import { useCallback, useEffect, useRef, useState } from "react";
import { Certificate } from "../../types/newtypes";
import { useGetCertificatesQuery } from "../../store/api/postgresCertificatesApi";
import { useNavigate } from "react-router-dom";
import { useAuth } from "../../supabase/AuthProvider";
import { useToast } from "../../context/CustomToast";

export const useCertificatesHook = () => {
  const { session } = useAuth();
  const { showToast } = useToast();
  const activeToken = useRef(session?.access_token);
  activeToken.current = session?.access_token;
  useEffect(() => {
    activeToken.current = session?.access_token;
    return () => {activeToken.current = undefined;};
  }, [session?.access_token]);
  const [pollWhilePending, setPollWhilePending] = useState(false);
  const {
    data: certificates = [],
    isLoading: loading,
    error,
  } = useGetCertificatesQuery(undefined, {
    pollingInterval: pollWhilePending ? 10000 : 0,
    skipPollingIfUnfocused: true,
    refetchOnMountOrArgChange: true,
  });

  const navigate = useNavigate();

  useEffect(() => {
    setPollWhilePending(certificates.some((cert) => !cert.download_uri));
  }, [certificates]);

  const navigateHome = useCallback(() => {
    navigate("/");
  }, [navigate]);

  const navigateToCourses = useCallback(() => {
    navigate("/courses");
  }, [navigate]);

  const handleViewCertificate = useCallback(
    (certificate: Certificate) => {
      navigate(`/certificates/${certificate.id}`, { state: { certificate } });
    },
    [navigate],
  );

  const handleDownloadCertificate = useCallback(async (certificate: Certificate) => {
    const token = session?.access_token;
    if (!certificate?.download_uri || !token) {return;}
    try {
      const api = (process.env.REACT_APP_BACKEND_BASE_URL || "").replace(/\/$/, "");
      const response = await fetch(`${api}/certificates/owner-artifacts/${encodeURIComponent(certificate.id)}/pdf`, {
        headers: {Authorization: `Bearer ${token}`},
      });
      if (!response.ok || response.headers.get("content-type")?.split(";")[0].trim().toLowerCase() !== "application/pdf") {
        throw new Error("Certificate download unavailable");
      }
      const blob = await response.blob();
      if (activeToken.current !== token) {return;}
      const url = URL.createObjectURL(blob);
      const link = document.createElement("a");
      link.href = url; link.download = "certificate.pdf";
      document.body.appendChild(link); link.click(); link.remove();
      const revoke = URL.revokeObjectURL.bind(URL);
      window.setTimeout(() => revoke(url), 1000);
    } catch {
      if (activeToken.current === token) {showToast("Certificate download could not be completed.", "error");}
    }
  }, [session?.access_token, showToast]);

  return {
    certificates,
    loading,
    error: error ? "Failed to fetch certificates" : null,
    handleViewCertificate,
    handleDownloadCertificate,
    navigateHome,
    navigateToCourses,
  };
};
